import { Component, EventEmitter, Input, Output } from '@angular/core';

import { PagedList } from '../../alarms-api';

/**
 * Koniec listy dociąganej przewijaniem: postęp, wskaźnik doklejania, ponowienie po
 * awarii i wartownik zamawiający kolejną porcję.
 *
 * Powstało z sześciu identycznych kopii tego samego bloku w jednym szablonie. Każda
 * kopia była okazją do pominięcia jednego z elementów, a najłatwiej pomijało się
 * przycisk ponowienia — bez którego lista po nieudanym doładowaniu stoi aż do
 * przeładowania strony (wartownik zgłasza się na PRZEJŚCIU przez próg, a lista nie
 * urosła, więc progu nie przecina).
 *
 * Cała treść to klasy globalne (`loading-dots` ze `styles.scss`, reszta z bootstrapa),
 * więc komponent nie wnosi własnych stylów.
 *
 * Umieszczaj jako OSTATNIE dziecko kontenera ze scrollem — wartownik ma zgłosić się
 * dopiero na końcu treści. W tabelach znaczy to „za `</table>`": elementu innego niż
 * wiersz przeglądarka nie zostawi w `tbody`, tylko wyrzuci go ponad tabelę, i wartownik
 * wjechałby w widok od razu.
 */
@Component({
  selector: 'app-scroll-footer',
  template: `
    <!-- Licznik bez klucza i18n: same cyfry z ukośnikiem znaczą to samo w każdym języku. -->
    <p *ngIf="list.showsProgress" class="text-muted small mb-0">{{ list.items.length }} / {{ list.total }}</p>
    <div *ngIf="list.isAppending" class="loading-dots" role="status">
      <span class="dot"></span><span class="dot"></span><span class="dot"></span>
      <span class="sr-only">{{ 'COMMON.LOAD_MORE' | translate }}</span>
    </div>
    <p *ngIf="showsRetry" class="text-danger small">
      {{ 'BACKEND.COULDNT-GET-DATA' | translate }}
      <span *ngIf="errorDetail" class="text-muted">({{ errorDetail }})</span>
      <button data-event="EVENTS.SHARED.COMPONENTS.SCROLL-FOOTER.BUTTON.COMMON-LOAD_MORE" type="button" class="btn btn-sm btn-outline-secondary ml-2" (click)="loadMore.emit()">
        {{ 'COMMON.LOAD_MORE' | translate }}
      </button>
    </p>
    <div appScrollSentinel [scrollRoot]="scrollRoot" [loadedCount]="list.items.length"
         (reached)="loadMore.emit()"></div>
  `
})
export class ScrollFooterComponent {

  /** Lista, której koniec obsługujemy — stąd postęp, stan doklejania i błąd. */
  @Input() list: PagedList;

  /**
   * Kontener ze scrollem tej listy. Bez niego wartownik patrzy na okno, więc lista
   * w ramce o stałej wysokości dociągałaby się od przewijania CAŁEJ strony.
   */
  @Input() scrollRoot: Element;

  /** Techniczny szczegół awarii — trasa i status. Bez tłumaczenia: czyta go tylko dev. */
  @Input() errorDetail: string = null;

  /** Zamówienie kolejnej porcji: z wartownika albo z przycisku ponowienia. */
  @Output() loadMore = new EventEmitter<void>();

  /**
   * Awaria PUSTEJ listy należy do `app-list-state`, które zajmuje wtedy miejsce wierszy.
   * Stopka jest końcem listy, a lista, której nie ma, nie ma końca — dwa przyciski
   * ponowienia jeden pod drugim czyta się jak usterka, a nie jak wybór.
   */
  get showsRetry(): boolean {
    return !!this.list.error && this.list.items.length > 0;
  }
}
